/* eslint-disable @typescript-eslint/no-explicit-any */
// context wrapper component
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { PropsWithChildren, createContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchTagMembers, fetchTagNames, syncGroupsFn } from 'services/queryServices';
import { OutputLogInterface, TagMemberType, TagType, isSentImageResult, isSentMessageResult, sentImageResultSchema, sentMessageResultSchema } from 'type/type';
import { z } from 'zod';

interface WhapiContextInterface {
  groups: TagType[];
  setGroups: React.Dispatch<React.SetStateAction<TagType[]>>;

  tagNames: string[];
  setTagNames: React.Dispatch<React.SetStateAction<string[]>>;

  ready: boolean;
  setReady: React.Dispatch<React.SetStateAction<boolean>>;

  excludedGroups: TagType[];
  setExcludedGroups: React.Dispatch<React.SetStateAction<TagType[]>>;

  advanceGroups: TagType[];
  setAdvanceGroups: React.Dispatch<React.SetStateAction<TagType[]>>;

  selectedTags: string[];
  setSelectedTags: React.Dispatch<React.SetStateAction<string[]>>;

  selectedGroups: TagMemberType[];
  setSelectedGroups: React.Dispatch<React.SetStateAction<TagMemberType[]>>;

  output: OutputLogInterface[];
  setOutput: React.Dispatch<React.SetStateAction<OutputLogInterface[]>>;

  preprocessOutput: (item: unknown) => void;
  groupFN: (id: string) => string;

  syncGroupsFunction: () => void;
}

export const WhapiContext = createContext<WhapiContextInterface>({
  groups: [],
  setGroups: () => [],
  tagNames: [],
  setTagNames: () => [],
  ready: false,
  setReady: () => false,
  excludedGroups: [],
  setExcludedGroups: () => [],
  advanceGroups: [],
  setAdvanceGroups: () => [],
  selectedTags: [],
  setSelectedTags: () => [],
  selectedGroups: [],
  setSelectedGroups: () => [],
  output: [],
  setOutput: () => [],
  preprocessOutput: () => [],
  groupFN: () => '',
  syncGroupsFunction: () => [],
});

export const WhapiProvider = ({ children }: PropsWithChildren<{}>) => {
  const [groups, setGroups] = useState<TagType[]>([]);
  const [tagNames, setTagNames] = useState<string[]>([]);

  const [ready, setReady] = useState(false);

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<TagMemberType[]>([]);

  const [excludedGroups, setExcludedGroups] = useState<TagType[]>([]);
  const [advanceGroups, setAdvanceGroups] = useState<TagType[]>([]);

  const [output, setOutput] = useState<OutputLogInterface[]>([]);

  const preprocessOutput = (output: unknown) => {
    const alwaysArray: any[] | z.infer<typeof sentMessageResultSchema>[] = Array.isArray(output) ? output : [output];
    const parsedOutput: OutputLogInterface[] = [];
    alwaysArray.map(item => {
      const ts = `${(isSentMessageResult(item) ? new Date(item.message.timestamp * 10000) : new Date()).toLocaleString()}`;
      const validatedMessage = isSentMessageResult({ ...item, message: { ...item.message, timeStamp: ts } });
      const validatedImage = isSentImageResult({ ...item, message: { ...item.message, timeStamp: ts } });
      if (validatedMessage) {
        return parsedOutput.push({
          ts: ts,
          content: item as z.infer<typeof sentMessageResultSchema>,
        });
      }
      console.log(validatedImage);
      if (validatedImage) {
        return parsedOutput.push({
          ts: ts,
          image: item as z.infer<typeof sentImageResultSchema>,
        });
      }
      if (typeof item === 'string')
        return parsedOutput.push({
          ts: ts,
          message: item,
        });
      return parsedOutput.push({
        ts: ts,
        message: 'Unrecognized Output',
      });
    });
    setOutput(prev => [...prev, ...parsedOutput]);
  };
  const queryClient = useQueryClient();
  const groupFN = (id: string): string => {
    const foundGroup = groups
      .map(group => group.members.find(member => member.RowKey === id))
      .flat()
      .filter(item => item !== undefined);

    return foundGroup[0]?.fn ?? 'No group found';
  };

  const syncGroupsFunction = async () => {
    const data = await syncGroupsFn();
    if (data.status === 200 && data.data !== 'No new groups found') {
      toast.warning('New groups found, the List will be updated shortly');
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
    } else {
      toast.success('No New groups found');
    }
    return data;
  };

  const states = {
    excludedGroups,
    setExcludedGroups,
    advanceGroups,
    setAdvanceGroups,
    groups,
    setGroups,
    tagNames,
    setTagNames,
    ready,
    setReady,
    selectedTags,
    setSelectedTags,
    selectedGroups,
    setSelectedGroups,
    output,
    setOutput,
    preprocessOutput,
    groupFN,
    syncGroupsFunction,
  };

  const { data: tagNamesResponse } = useQuery({ queryKey: ['tags'], queryFn: fetchTagNames, retry: 3 });
  const { data: groupDataResponse } = useQuery({ queryKey: ['groups'], queryFn: () => Promise.all(tagNames.map(tag => fetchTagMembers(tag))), retry: 3, enabled: tagNames?.length > 0 });

  useEffect(() => {
    if (tagNamesResponse && tagNamesResponse.status === 200) {
      setTagNames(tagNamesResponse.data.tags);
    }
  }, [tagNamesResponse]);
  useEffect(() => {
    if (groupDataResponse?.every(response => response.status === 200)) {
      const newGroupData = groupDataResponse?.map((response, index) => ({
        name: tagNames[index],
        members: response.data,
      }));
      setGroups(newGroupData);
    }
  }, [groupDataResponse]);

  useEffect(() => {
    if (tagNames.length > 0 && groups.length > 0) {
      setReady(true);
    } else {
      setReady(false);
    }
  }, [tagNames, groups]);

  return <WhapiContext.Provider value={states}>{children}</WhapiContext.Provider>;
};

export const useWhapiGroups = (rowKey?: string): TagType[] | null => {
  const { groups, ready } = useWhapi();

  const filteredGroup = React.useMemo(() => {
    if (!ready) return null;
    if (!rowKey) return groups;
    return groups.filter(group => group.members.map(member => member.RowKey === rowKey));
  }, [groups, rowKey, ready]);
  return filteredGroup;
};

export const useWhapiTags = (): string[] | null => {
  const { tagNames, ready } = useWhapi();

  return React.useMemo(() => {
    if (!ready || !tagNames) return null;
    return tagNames;
  }, [tagNames, ready]);
};

export const useWhapi = (): WhapiContextInterface => {
  const context = React.useContext(WhapiContext);

  if (context === undefined) {
    throw new Error('useWhapi must be used within a WhapiProvider');
  }
  return context;
};
